import type { MergeComparison } from '@festina/configuration-general-types/configuration'
import { ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetMergeComparison } from '@festina/configuration-general-types/configuration/hooks/ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetMergeComparison'
import type { BranchKey } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { useCallback, useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import { extractConfigurationOperationsMergeConflicts } from './helpers'

const useFetchMergeComparison =
  ConfigurationRepositoryComparisonsService_configurationComparisonServiceGetGetMergeComparison.useSuspenseQuery

type Props = {
  branchKey: BranchKey
  baseBranchKey: Optional<BranchKey>
}

const toConflictsWithSourceInformation = (
  mergeComparison: Optional<MergeComparison>,
  rootBlock: string,
) =>
  extractConfigurationOperationsMergeConflicts(
    mergeComparison?.conflicts?.filter(notEmpty),
    rootBlock,
  )

export const useRebaseBranchConflictsWithBaseBranch = ({
  branchKey,
  baseBranchKey,
}: Props) => {
  const { data, refetch } = useFetchMergeComparison(
    {
      componentName: branchKey.server.toLocaleLowerCase(),
      lhsBranchName: branchKey.name,
      rhsBranchName: baseBranchKey?.name ?? '',
    },
    {
      staleTime: 0,
      refetchOnReconnect: 'always',
      refetchOnMount: 'always',
      enabled: !!baseBranchKey?.name,
    },
  )
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name

  const conflicts = useMemo(
    () => toConflictsWithSourceInformation(data, rootBlock),
    [data, rootBlock],
  )

  /** Fetches the merge comparison again and resolves with the latest conflicts. */
  const checkConflicts = useCallback(async () => {
    const { data: latestData } = await refetch({ throwOnError: true })

    return toConflictsWithSourceInformation(latestData, rootBlock).rightHandSide
  }, [refetch, rootBlock])

  // For rebases we're only interested in conflicts coming in from the base branch, which are represented on the right-hand side.
  return { conflicts: conflicts.rightHandSide, checkConflicts }
}
